import { Link } from "zudoku/components";
import {
  buildMethods,
  getBuild,
  getMethod,
  stepHref,
  tutorialSteps,
} from "./data.js";
import { WizardProgress } from "./WizardProgress.js";

interface WizardStepsProps {
  /** Build id from `BUILDS`. Omit on the build picker. */
  build?: string;
  /** Method id from `METHODS`. Omit before a method is chosen. */
  method?: string;
  /** Step slug for a tutorial split across pages. */
  step?: string;
}

/** Shared progress and context, including prerequisites for direct arrivals. */
export function WizardSteps({
  build: buildId,
  method: methodId,
  step: stepSlug,
}: WizardStepsProps) {
  const build = buildId ? getBuild(buildId) : undefined;
  const method = methodId ? getMethod(methodId) : undefined;

  if (buildId && !build) {
    throw new Error(
      `Unknown build "${buildId}" — add it to BUILDS in src/components/getting-started/data.ts`,
    );
  }

  if (methodId && !method) {
    throw new Error(
      `Unknown method "${methodId}" — add it to METHODS in src/components/getting-started/data.ts`,
    );
  }

  const phase = method ? 3 : build ? 2 : 1;

  const steps =
    buildId && methodId ? tutorialSteps(buildId, methodId) : undefined;
  const stepIndex = steps?.findIndex((s) => s.slug === stepSlug) ?? -1;
  const currentStep = steps?.[stepIndex];
  const prerequisiteStep = steps?.find(
    (s) => s.slug === currentStep?.prerequisiteStep,
  );

  return (
    <>
      <WizardProgress
        phase={phase}
        skipMethod={!!buildId && buildMethods(buildId).length === 1}
        buildLabel={build?.label}
        buildHref={build ? "/getting-started" : undefined}
        methodLabel={method?.label}
        methodHref={build && method ? `/getting-started/${buildId}` : undefined}
        tutorialLabel={
          steps && stepIndex >= 0
            ? `Step ${stepIndex + 1} of ${steps.length}`
            : undefined
        }
      />
      {build && method && (
        <section
          aria-label="About this tutorial"
          className="not-prose mb-6 rounded-xl border border-border bg-muted/25 px-5 py-4"
        >
          <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              {build.label} tutorial
            </span>
            <span aria-hidden="true">/</span>
            <span>{method.label}</span>
            {steps && stepIndex >= 0 && (
              <>
                <span aria-hidden="true">/</span>
                <span>
                  Step {stepIndex + 1} of {steps.length}
                </span>
              </>
            )}
          </p>
          <p className="mb-0 mt-2 text-sm leading-relaxed text-foreground">
            {currentStep?.description ?? build.summary}
          </p>
          {currentStep?.prerequisite && (
            <p className="mb-0 mt-3 border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">
                Before you start:{" "}
              </span>
              {currentStep.prerequisite}{" "}
              {prerequisiteStep && (
                <Link
                  className="text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
                  to={stepHref(build.id, method.id, prerequisiteStep.slug)}
                >
                  {prerequisiteStep.title}
                </Link>
              )}
            </p>
          )}
        </section>
      )}
    </>
  );
}
